import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Activity,
} from 'lucide-react'
import type { Database } from '@limo/database'

type GHLSyncLog = Database['public']['Tables']['ghl_sync_logs']['Row']

interface GHLSyncLogsSectionProps {
  bookingId?: string
  quoteId?: string
}

// Human-readable action labels for each (action, trigger_stage) pair.
// Keyed by `${action}:${trigger_stage}`; falls back to action alone.
const ACTION_LABELS: Record<string, string> = {
  'create_quote_request:quote_request': 'Step 1: Quote Request Created',
  'create_quote_request:share_quote': 'Share Quote: Opportunity Created',
  'update_quote:select_vehicle': 'Step 2: Vehicle Selected, Opportunity Updated',
  'update_quote:share_quote': 'Share Quote: Opportunity Updated',
  'create:continue_payment': 'Step 3: Continue to Payment',
  'update_booked:booking_confirmed': 'Step 4: Payment Succeeded, Marked as Booked',
}

const ACTION_FALLBACK_LABELS: Record<string, string> = {
  create_quote_request: 'Create Quote Opportunity',
  update_quote: 'Update Quote Opportunity',
  create: 'Create Booking Opportunity',
  update_booked: 'Mark Opportunity as Booked',
}

function formatActionLabel(log: GHLSyncLog): string {
  const key = `${log.action}:${log.trigger_stage ?? ''}`
  return ACTION_LABELS[key] ?? ACTION_FALLBACK_LABELS[log.action] ?? log.action
}

const ERROR_SOURCE_LABELS: Record<string, string> = {
  ghl_api: 'GHL API Error',
  ghl_auth: 'GHL Auth Failed',
  ghl_rate_limit: 'Rate Limited',
  missing_config: 'Missing Config',
  viewer_network: 'Network Failure',
  edge_function: 'Edge Function Error',
}

function formatErrorSource(source: string | null): string {
  if (!source) return 'Unknown'
  return ERROR_SOURCE_LABELS[source] ?? source
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.round((now - then) / 1000)
  if (diffSec < 60) return `${diffSec}s ago`
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return new Date(iso).toLocaleDateString()
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/**
 * Display the GoHighLevel sync history for a specific booking or quote.
 * Shows every sync attempt (successful and failed) in reverse-chronological
 * order, with collapsible details containing the request/response payloads.
 *
 * ADMIN-ONLY: This section is hidden from non-admin users. The check happens
 * inside the component (rather than at the mount site) so every future mount
 * point inherits the policy by default — the fail-safe choice for access
 * control. RLS on ghl_sync_logs still provides authoritative data isolation
 * regardless of this UI gate.
 */
export function GHLSyncLogsSection({ bookingId, quoteId }: GHLSyncLogsSectionProps) {
  const { isAdmin } = useAuth()
  const [showFailuresOnly, setShowFailuresOnly] = useState(false)

  const { data: logs, isLoading, error } = useQuery<GHLSyncLog[]>({
    queryKey: ['ghl-sync-logs', bookingId ?? quoteId],
    queryFn: async () => {
      let query = supabase
        .from('ghl_sync_logs')
        .select('*')
        .order('created_at', { ascending: false })

      if (bookingId) {
        query = query.eq('booking_id', bookingId)
      } else if (quoteId) {
        query = query.eq('booking_quote_id', quoteId)
      }

      const { data, error } = await query
      if (error) throw error
      return data ?? []
    },
    // Skip the query entirely for non-admins so we don't waste a request.
    // All hooks above this point run unconditionally (React rules-of-hooks);
    // the early return below only affects rendering.
    enabled: isAdmin && !!(bookingId || quoteId),
  })

  if (!isAdmin) return null

  const filteredLogs =
    logs?.filter((log) => (showFailuresOnly ? log.status === 'failed' : true)) ?? []

  const failureCount = logs?.filter((log) => log.status === 'failed').length ?? 0

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5" />
            GoHighLevel Sync History
            {logs && logs.length > 0 && (
              <Badge variant="outline" className="ml-1">
                {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
              </Badge>
            )}
            {failureCount > 0 && (
              <Badge variant="destructive">
                {failureCount} failed
              </Badge>
            )}
          </CardTitle>
          {logs && logs.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showFailuresOnly}
                onChange={(e) => setShowFailuresOnly(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300"
              />
              Show failures only
            </label>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">
            Failed to load sync history: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        )}

        {!isLoading && !error && filteredLogs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {logs && logs.length > 0
              ? 'No failed sync attempts.'
              : bookingId
                ? 'No sync history for this booking.'
                : 'No sync history for this quote.'}
          </p>
        )}

        {filteredLogs.length > 0 && (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <GHLSyncLogRow key={log.id} log={log} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface GHLSyncLogRowProps {
  log: GHLSyncLog
}

function GHLSyncLogRow({ log }: GHLSyncLogRowProps) {
  const [isOpen, setIsOpen] = useState(false)
  const isSuccess = log.status === 'success'

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={`rounded-md border ${
          isSuccess ? 'border-border bg-background' : 'border-destructive/30 bg-destructive/5'
        }`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-start gap-3 p-3 text-left hover:bg-muted/50 transition-colors rounded-md"
          >
            <div className="mt-0.5">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isSuccess ? (
                  <Badge variant="default" className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Success
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Failed
                  </Badge>
                )}
                <span className="text-sm font-medium">{formatActionLabel(log)}</span>
                {log.http_status_code != null && (
                  <span className="text-xs text-muted-foreground">
                    HTTP {log.http_status_code}
                  </span>
                )}
              </div>
              {!isSuccess && log.error_message && (
                <p className="text-sm text-destructive mt-1 break-words">
                  {log.error_message}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span title={formatAbsoluteTime(log.created_at)}>
                  {formatRelativeTime(log.created_at)}
                </span>
                {!isSuccess && log.error_source && (
                  <>
                    <span>·</span>
                    <Badge variant="outline" className="text-xs">
                      {formatErrorSource(log.error_source)}
                    </Badge>
                  </>
                )}
                {log.ghl_opportunity_id && (
                  <>
                    <span>·</span>
                    <span className="font-mono text-xs">
                      opp: {log.ghl_opportunity_id.slice(0, 8)}…
                    </span>
                  </>
                )}
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t px-3 py-2 space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Action:</span>{' '}
                <span className="font-mono">{log.action}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Trigger stage:</span>{' '}
                <span className="font-mono">{log.trigger_stage ?? '—'}</span>
              </div>
              {log.ghl_contact_id && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Contact ID:</span>{' '}
                  <span className="font-mono break-all">{log.ghl_contact_id}</span>
                </div>
              )}
              {log.ghl_opportunity_id && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Opportunity ID:</span>{' '}
                  <span className="font-mono break-all">{log.ghl_opportunity_id}</span>
                </div>
              )}
              <div className="col-span-2">
                <span className="text-muted-foreground">Timestamp:</span>{' '}
                {formatAbsoluteTime(log.created_at)}
              </div>
            </div>
            {log.request_payload != null && (
              <details>
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Request payload
                </summary>
                <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto text-[10px] leading-tight">
                  {JSON.stringify(log.request_payload, null, 2)}
                </pre>
              </details>
            )}
            {log.response_body != null && (
              <details>
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Response body
                </summary>
                <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto text-[10px] leading-tight">
                  {JSON.stringify(log.response_body, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
